<template>
  <div>
    <div class="row mt-3 pb-4 border-bottom">
      <div class="col-12 col-md-5">
        <div class="color-secondary-6 font-14 mb-1">
          Marca:
        </div>
        <div class="d-flex align-items-center">
          <div class="row w-100">
            <div class="col-12 col-md-9">
              <validation-observer ref="newBrandObserver">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-input
                    v-model="newBrand"
                    custom-error-msg="Este campo es requerido"
                    placeholder="Escriba Marca"
                    :error="errors[0]"
                    @keypress.enter.native="addBrand"
                  />
                </validation-provider>
              </validation-observer>
            </div>
            <div class="col px-md-0 mt-md-0 mt-2">
              <action-button
                variant="success w-100"
                type="button"
                i="fas fa-file-plus"
                @click="addBrand"
              >
                Añadir
              </action-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-for="(brand, brandIndex) in brands" :key="brandIndex" class="my-3">
      <div class="my-4">
        <span class="px-3 py-2 rounded-pill brand-pill">
          {{ brand.name }}

          <i
            v-if="!brand.sealed"
            class="far fa-times-circle pointer color-red mt-1"
            @click="removeBrand(brandIndex)"
          ></i>
        </span>
      </div>

      <div class="mt-3 pb-4 border-bottom">
        <div class="row">
          <div class="col-12 col-md-4 pr-4">
            <div class="color-secondary-6 font-14 mb-1">
              Modelo:
            </div>
            <validation-observer :ref="`brand-${brandIndex}-model-observer`">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="brand.input"
                  custom-error-msg="Este campo es requerido"
                  placeholder="Escriba Modelo"
                  :error="errors[0]"
                  @keypress.enter.native="addModel(brand, brandIndex)"
                />
              </validation-provider>
            </validation-observer>

            <div class="d-flex justify-content-end mt-2">
              <action-button
                :variant="isMobile ? 'success w-100 mb-2' : 'success'"
                type="button"
                i="fas fa-file-plus"
                @click="addModel(brand, brandIndex)"
              >
                Añadir
              </action-button>
            </div>
          </div>
          <div class="col-12 col-md pl-4" :class="{ 'border-left': !isMobile }">
            <div class="color-secondary-6 font-14 mb-1">
              Modelo(s) Asignados(s):
              <div class="d-flex flex-wrap">
                <div
                  class="p-2 rounded-pill detail-pill my-1 mr-2"
                  v-for="(model, modelIndex) in brand.models"
                  :key="modelIndex"
                >
                  {{ model.name }}
                  <i
                    v-if="!model.sealed"
                    class="far fa-times-circle pointer color-red pt-1"
                    @click="removeModel(brand, modelIndex)"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import mixAlert from '@/mixins/alert.mixin';
import { name as appModuleName } from '@/modules/app/store/index';
import { mapGetters } from 'vuex';

export default {
  name: 'PropertyBrand',
  mixins: [mixAlert],
  components: {
    CustomInput,
    ActionButton,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    property: {
      type: Object,
      required: true,
    },
    modelId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    newBrand: null,
    brands: [],
  }),
  computed: {
    ...mapGetters(appModuleName, {
      isMobile: 'getIsMobileDensity',
    }),
  },
  mounted() {
    this.brands = this.property.details.map((b) => ({
      input: null,
      ...b,
      models: b.children,
    }));
  },
  methods: {
    async addBrand() {
      const observer = this.$refs['newBrandObserver'];
      const valid = await observer.validate();
      if (!valid) return;

      if (this.brands.some((x) => x.name === this.newBrand)) {
        this.ShowToast(
          'Alerta',
          `La marca <b>${this.newBrand}</b> ya está en la lista de Marca(s) Seleccionada(s).`,
          'error'
        );
        return;
      }

      this.brands.push({
        name: this.newBrand,
        input: null,
        models: [],
      });
      this.newBrand = null;
      await observer.reset();
      this.onChange();
    },
    async removeBrand(index) {
      if (this.brands[index].models.length > 0) {
        const confirmed = await this.ShowCancelModal(
          'Confirmación',
          `Se removerán todos los modelos agregados a la marca <b>${this.brands[index].name}</b>. ¿Desea continuar?`,
          'Sí',
          'No'
        );
        if (!confirmed) return;
      }

      this.brands.splice(index, 1);
      this.onChange();
    },
    async addModel(brand, brandIndex) {
      const observer = this.$refs[`brand-${brandIndex}-model-observer`][0];
      const valid = await observer.validate();
      if (!valid) return;

      if (brand.models.some((x) => x.name === brand.input)) {
        this.ShowToast(
          'Alerta',
          `El modelo <b>${brand.input}</b> ya está en la lista de Modelos(s) Seleccionado(s).`,
          'error'
        );
        return;
      }

      brand.models.push({
        parentId: brand.id,
        name: brand.input,
      });
      brand.input = null;
      await observer.reset();
      this.onChange();
    },
    async removeModel(brand, modelIndex) {
      brand.models.splice(modelIndex, 1);
      this.onChange();
    },
    onChange() {
      this.property.details = this.brands.map((b) => ({
        propertyId: this.property.id,
        ...b,
        children: b.models.map((m) => ({
          propertyId: this.modelId,
          ...m,
        })),
      }));
    },
  },
};
</script>

<style scoped>
.gap-x-1 {
  gap: 0px 10px;
}
.brand-pill {
  background-color: #d9e5df;
  border: 1px solid #c3c3c3;
  font-weight: 600;
}
.detail-pill {
  background-color: white;
  border: 1px solid black;
  font-weight: 600;
  color: black;
}
.pointer {
  cursor: pointer;
}
</style>
